import { featureCollection, point } from '@turf/helpers'
import get from 'lodash/get'
import has from 'lodash/has'
import isArray from 'lodash/isArray'
import isNil from 'lodash/isNil'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import memoize from 'lodash/memoize'
import trim from 'lodash/trim'
import OLFormatGeoJSON from 'ol/format/GeoJSON'
import { all as OLLoadingStrategyAll } from 'ol/loadingstrategy'
import OLSourceVector from 'ol/source/Vector'

import PoziAPIData from 'api/data'
import * as PoziUtils from 'api/utils'
import LoaderHandleGeoJSON, { LoaderFailed } from 'config/LoaderHandleGeoJSON'

// A (Adapter)-> PoziUtils function is implemented in order to follow the standard Adapter pattern
const PoziJotForm = memoize(function () {
  // Adapters
  const adapters = {
    /**
     * @api {NA} PoziJotForm().loader OpenLayers loader
     * @apiDescription Define the Openlayers loader
     * @apiGroup JotForm
     * @apiName loader
     *
     * @apiParam {Object} Layer Pozi Layer
     *
     * @apiSuccess {Object} olSourceVector OpenLayers SourceVector
     * @apiError {Object} Error null
     */
    loader: function (layer) {
      return loader(layer)
    },
    /**
     * @api {NA} PoziJotForm().getGeoJSON Get JotForm data as GeoJSON
     * @apiDescription Pass JotForm URL to get data as GeoJSON
     * @apiGroup JotForm
     * @apiName getGeoJSON
     *
     * @apiParam {String} URL JotForm url
     * @apiParam {Boolean} bUseText When generating the GeoJSON use text as the property name.
     *
     * @apiSuccess {Promise} Resolve No errors
     * @apiError {Promise} Reject Error
     */
    getGeoJSON: function (url, bUseText) {
      return getGeoJSON(url, bUseText)
    }
  }

  var loader = function (layer) {
    if (isNil(layer)) {
      console.error('Invalid JotForm layer.')
      return null
    }

    if (!has(layer, 'titleid')) {
      console.error('No titleid on JotForm layer.')
      return null
    }

    if (!has(layer, 'config.spatial')) {
      console.error('Cannot load JotForm layer without spatial options.')
      return null
    }

    if (get(layer, 'config.spatial.loader', '') !== 'jotform') {
      console.error('Invalid layer loader type for JotForm.')
      return null
    }

    const options = {
      format: new OLFormatGeoJSON(),
      crossOrigin: 'Anonymous',
      loader: function (extent, resolution, projection, success, failure) {
        let url = get(layer, 'config.spatial.url', null)
        url = url.replace('[timestampToNearestOneMin]', PoziUtils.timestampRoundedUpToMinutes(2))
        if (isNil(url)) {
          console.error('Unable to find JotForm url. Expecting layer.config.spatial.url')
          return
        }
        const bUseText = true
        return getGeoJSON(url, bUseText)
          .then(function (geojson) {
            LoaderHandleGeoJSON(geojson, projection, olSourceVector, layer, success)
            // TODO: Check if feature directive is in URL.. if so, highlight it. feature[layername,layerid]
          })
          .catch(function (err) {
            LoaderFailed(layer, err, failure)
          })
      },
      strategy: OLLoadingStrategyAll,
      projection: 'EPSG:3857'
    }

    const olSourceVector = new OLSourceVector(options)

    return olSourceVector
  }

  /**
   * Handle data requests
   */
  var getGeoJSON = function (url, bUseText) {
    return new Promise(function (resolve, reject) {
      let err = ''
      if (isNil(url)) {
        err = 'Invalid url.'
        return reject(err)
      }

      console.log('JotForm url', url)

      PoziAPIData().ajax({
        url,
        error: function (err) {
          console.log('JotForm ajax error: ', err)
          reject(err)
        },
        success: function (jotFormData) {
          convertJotFormToGeoJSON(jotFormData, bUseText)
            .then(function (geojson) {
              resolve(geojson)
            })
            .catch(function (err) {
              console.log('JotForm parse error: ', err)
              reject(err)
            })
        }
      })
    })
  }

  /**
   * Handle data requests
   * @param jotFormData (string directly from jotFormData API)
   * @return FeatureCollection
   */
  var convertJotFormToGeoJSON = function (jotFormData, bUseText) {
    return new Promise(function (resolve, reject) {
      let err = ''
      if (isNil(bUseText)) {
        bUseText = true
      }
      const features = []
      const fc = featureCollection(features)

      if (isNil(jotFormData)) {
        err = 'Invalid jotFormData object.'
        return reject(err)
      }
      const content = get(jotFormData, 'content', [])
      if (content.length === 0) {
        console.log('No features found.')
        return resolve(fc)
      }

      for (const contentKey in content) {
        const cont = content[contentKey]

        const status = get(cont, 'status', '')
        if (status === 'DELETED') {
          continue
        }

        const properties = {}
        const created = get(cont, 'created_at', '')
        if (!isNil(created)) {
          properties.created_at = created
        }
        const updated = get(cont, 'updated_at', '')
        if (!isNil(updated)) {
          properties.updated_at = updated
        }

        const answers = get(cont, 'answers', null)
        if (isNil(answers)) {
          console.log('Answers data not found', cont)
          continue
        }
        let lngValue = 0
        let latValue = 0
        for (const answersKey in answers) {
          const answersObj = answers[answersKey]

          if (isNil(answersObj)) {
            console.log('Answer data not found for answersKey', answersKey)
            continue
          }

          const type = get(answersObj, 'type', '')

          let prop = ''
          if (bUseText) {
            prop = get(answersObj, 'text', '')
          } else {
            prop = get(answersObj, 'name', '')
          }

          if (prop.toLowerCase() === 'longitude') {
            lngValue = get(answersObj, 'answer', '')
            continue
          }

          if (prop.toLowerCase() === 'latitude') {
            latValue = get(answersObj, 'answer', '')
            continue
          }

          // Find answer prop (sometimes is an array, or object, or pure string.)
          let answerText = ''
          let answerObj = get(answersObj, 'answer', '')
          // TESTING
          if (answersKey === '1') {
            // answerObj = { '0': 'test', a: 'test2' } // testing
            // answerObj = ['test', 'test2'] // testing
          }

          // First check if answerObj is a pure string, then add it (if not empty).
          if (isString(answerObj)) {
            answerObj = trim(answerObj)
            if (answerObj !== '') {
              if (prop === '') {
                prop = 'answer'
              }
              properties[prop] = answerObj
            }
            continue
          }

          // Second check if there is an attribute called prettyFormat, if it is a pure string, then use it.
          let prettyFormat = get(answersObj, 'prettyFormat', null)
          if (isString(prettyFormat) && prettyFormat !== '' && type !== 'control_fileupload') {
            properties[prop] = prettyFormat
            continue
          }

          // Check if answersObj is an array, if so, split it into seperate properties.
          if (isArray(answerObj)) {
            if (answerObj.length === 0) {
              continue
            }
            let arrCounter = 0
            for (const key in answerObj) {
              answerText = answerObj[key]
              // var answerFlat = flattenDeep(answerObj)
              // answerText = answerFlat.join(';')
              let propKeyInArray = PoziUtils.getJSONSafeString(prop)
              if (answerObj.length > 1) {
                arrCounter++
                propKeyInArray += PoziUtils.getJSONSafeString(arrCounter + '')
              }
              properties[propKeyInArray] = answerText
            }
            continue
          }

          // Check if answersObj is an object, if so, create a prettyFormat style representation.
          if (isObject(answerObj)) {
            let count = 0
            prettyFormat = ''
            for (const key2 in answerObj) {
              if (Object.hasOwnProperty.call(answerObj, key2)) {
                prettyFormat += answerObj[key2]
                if (count > 0) {
                  prettyFormat += ';'
                }
                count++
              }
            }
            prettyFormat = trim(prettyFormat)
            if (prettyFormat !== '') {
              properties[prop] = prettyFormat
            }
            continue
          }

          // End of answers array.
        }

        const pt = point([lngValue * 1, latValue * 1], properties)
        features.push(pt)
      }

      return resolve(fc)
    })
  }

  return adapters // Return adapters (must be at end of adapter)
})

export default PoziJotForm
// End A (Adapter)
